import { TaskType } from "__graphql__/types";
import { AppLanguage } from "app/stores/useAppLanguageStore";
import { countryNameFromCode } from "utils/countryNames";
import { getShelfDetail } from "utils/item";

import { Check, PriorityTag, RawCheckWithProduct, TranslatedCheck } from "./types";

export const tagToPriority = {
  low: [1],
  medium: [2, 3],
  top: [4, 5, 6],
  urgent: [7, 8],
} as Record<PriorityTag, number[]>;

export const getPriorityGroup = (priority: number) => {
  if (priority <= 1) {
    return tagToPriority.low;
  }
  if (priority <= 3) {
    return tagToPriority.medium;
  }
  if (priority <= 6) {
    return tagToPriority.top;
  }
  return tagToPriority.urgent;
};

// Priority is not used to narrow the search for these types — filtering by type is already
// specific enough — so they get an empty band, which leaves priority unfiltered.
const TYPES_NO_PRIO: ReadonlyArray<TaskType> = [
  TaskType.inventory_discrepancy_check,
  TaskType.eoy_inventory_check,
  TaskType.eoy_inventory_recount_check,
];

export const getTaskPriorityGroup = (type: TaskType[], priority: number): number[] => {
  if (type.some((t) => TYPES_NO_PRIO.includes(t))) {
    return [];
  }

  return getPriorityGroup(priority);
};

// Must stay in sync with tagToPriority above — an unmapped priority is dropped from the
// grouped checks list by useGroupedByPriorityTagChecks
export const priorityToTag = {
  1: "low",
  2: "medium",
  3: "medium",
  4: "top",
  5: "top",
  6: "top",
  7: "urgent",
  8: "urgent",
} as Record<number, PriorityTag>;

export const serializeCheck = (check: RawCheckWithProduct): Check => {
  const shelfDetail = check.shelfNumber ? getShelfDetail(check.shelfNumber) : null;
  const status = "status" in check ? (check.status ?? null) : null;

  return {
    checkId: check.id ?? null,
    productImageUrl: check.product?.imageUrl,
    productCountryOfOriginCode: check.product?.countryOfOrigin?.code?.toLocaleUpperCase() ?? null,
    productName: check.product?.name ?? null,
    shelfNumber: shelfDetail?.shelfNumber ?? null,
    shelfLetter: shelfDetail?.shelfLetter ?? null,
    sku: check.product?.sku ?? null,
    status,
    type: check.type ?? null,
    priority: check.priority ?? null,
    minDaysToBestBeforeDate: check.product?.minDaysToBestBeforeDate ?? null,
  };
};

export const serializeAndTranslateCheck = (
  rawCheck: RawCheckWithProduct,
  lang: AppLanguage,
): TranslatedCheck => {
  const check = serializeCheck(rawCheck);
  return {
    ...check,
    productCountryOfOriginName: countryNameFromCode(lang, check.productCountryOfOriginCode),
  };
};
